import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ChefHat, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import GoldCorners from "@/components/GoldCorners";

const STORAGE_KEY = "cs_welcome_shown_v1";
const SHOW_DELAY_MS = 1400;

const CARD_ICONS = [ChefHat, Sparkles];

export default function WelcomePopup() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY)) return;
    } catch (e) {
      // sessionStorage blocked (private mode) — still show once per page load
    }
    const showTimer = setTimeout(() => {
      setVisible(true);
      // trigger enter animation on next paint
      requestAnimationFrame(() => setEntering(true));
    }, SHOW_DELAY_MS);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible]);

  const close = () => {
    setEntering(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setTimeout(() => setVisible(false), 320);
  };

  if (!visible || !t.welcome) return null;

  return (
    <div
      data-testid="welcome-popup"
      aria-modal="true"
      role="dialog"
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6 transition-opacity duration-500 ${entering ? "opacity-100" : "opacity-0"}`}
    >
      {/* Backdrop */}
      <div
        data-testid="welcome-backdrop"
        onClick={close}
        aria-hidden
        className="absolute inset-0 bg-[#070E13]/85"
        style={{ backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      />

      {/* Panel */}
      <div
        className={`relative w-full max-w-3xl bg-[#0C1821] border border-[#C7A26C]/25 shadow-2xl transition-all duration-500 ${entering ? "translate-y-0 scale-100" : "translate-y-4 scale-[0.985]"}`}
        style={{ boxShadow: "0 40px 100px -20px rgba(7, 14, 19, 0.9), 0 0 0 1px rgba(199,162,108,0.08)" }}
      >
        {/* Subtle food-plating background — soft, shadowed, non-distracting.
            Clipped inside its own overflow-hidden wrapper so GoldCorners remain visible outside. */}
        <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <img
            src="https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/hzqinar3_IMG_2099.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.32, filter: "grayscale(0.25) brightness(0.7) contrast(1.05) saturate(0.9) blur(1.5px)" }}
          />
          {/* Deep blue base wash — preserves the site's blue identity */}
          <div className="absolute inset-0 bg-[#0C1821]/55" />
          {/* Radial vignette — focuses light near center, darkens edges */}
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 42%, rgba(12,24,33,0.35) 0%, rgba(12,24,33,0.78) 60%, rgba(7,14,19,0.94) 100%)" }} />
          {/* Warm golden chiaroscuro accent */}
          <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.22) 0%, transparent 45%, rgba(7,14,19,0.4) 100%)" }} />
          {/* Inner vignette for cinematic depth */}
          <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 180px 30px rgba(7,14,19,0.85), inset 0 0 70px 8px rgba(199,162,108,0.06)" }} />
        </div>

        <GoldCorners size={18} inset={-2} />

        {/* Close */}
        <button
          type="button"
          onClick={close}
          data-testid="welcome-close"
          aria-label="Close"
          className="absolute top-4 right-4 rtl:right-auto rtl:left-4 w-9 h-9 flex items-center justify-center text-[#F4EEE8]/60 hover:text-[#C7A26C] transition-colors z-20"
        >
          <X size={18} strokeWidth={1.6} />
        </button>

        <div className="relative z-10 px-6 sm:px-10 lg:px-14 py-10 sm:py-14">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto">
            <p className="overline gold-text tracking-[0.32em]">{t.welcome.eyebrow}</p>
            <h2 className="h-editorial text-3xl sm:text-4xl lg:text-[2.75rem] mt-5 leading-[1.12] text-[#F4EEE8]">
              {t.welcome.title}
            </h2>
            <p className="mt-5 text-sm sm:text-base text-[#D6D1C9] leading-relaxed">
              {t.welcome.lead}
            </p>
            <span aria-hidden className="block mx-auto mt-7 w-10 h-px bg-[#C7A26C]/60" />
          </div>

          {/* Two service cards */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {t.welcome.cards.map((c, i) => {
              const Icon = CARD_ICONS[i];
              return (
                <div
                  key={i}
                  data-testid={`welcome-card-${i}`}
                  className="relative bg-[#070E13] border border-[#C7A26C]/20 p-6 sm:p-7 hover:border-[#C7A26C]/55 transition-colors duration-500"
                >
                  <GoldCorners size={10} inset={-1} />
                  <div className="w-11 h-11 rounded-full border border-[#C7A26C]/50 flex items-center justify-center">
                    <Icon size={18} className="text-[#C7A26C]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl mt-5 text-[#F4EEE8] leading-snug">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-xs sm:text-sm text-[#D6D1C9] leading-relaxed">
                    {c.body}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-5">
            <Link
              to="/services"
              onClick={close}
              data-testid="welcome-cta"
              className="cta-premium inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-8 py-4 text-xs uppercase tracking-[0.24em] font-medium"
            >
              {t.welcome.cta} <ArrowRight size={14} />
            </Link>
            <button
              type="button"
              onClick={close}
              data-testid="welcome-dismiss"
              className="text-xs uppercase tracking-[0.24em] text-[#F4EEE8]/70 hover:text-[#C7A26C] transition-colors px-4 py-3"
            >
              {t.welcome.dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
